import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen,
  Check,
  Copy,
  Gavel,
  Loader2,
  ScrollText,
  Search,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const isReg = (h) => {
  if (h.document_kind === "regulation" || h.doc_type === "regulation") return true;
  return /^\s*(اللائحة|لائحة|اللائحه|لائحه|القرار|قرار)/.test((h.title || "").trim());
};

export default function UnifiedSearch() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [q, setQ] = useState(sp.get("q") || "");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [answer, setAnswer] = useState("");
  const [lawHits, setLawHits] = useState([]);
  const [principles, setPrinciples] = useState([]);
  const [tab, setTab] = useState("all");
  const [scope, setScope] = useState("all");
  const [copiedId, setCopiedId] = useState(null);

  const t = useMemo(() => ({
    title: isAr ? "البحث القانوني الموحّد" : "Unified legal search",
    lead: isAr
      ? "ابحث مرة واحدة في القوانين واللوائح ومبادئ محكمة التمييز. اكتب فكرتك أو سؤالك بالعربي أو باللهجة الكويتية ودع المستشار الذكي يجد الأقرب لموضوعك."
      : "Search laws, regulations and cassation principles at once.",
    placeholder: isAr ? "مثال: الفصل التعسفي من العمل، أو الطعن بالتزوير..." : "e.g. unfair dismissal…",
    btn: isAr ? "ابحث" : "Search",
    thinking: isAr ? "جارِ البحث الذكي..." : "Searching…",
    all: isAr ? "الكل" : "All",
    laws: isAr ? "القوانين" : "Laws",
    regs: isAr ? "اللوائح والقرارات" : "Regulations",
    princ: isAr ? "مبادئ التمييز" : "Cassation principles",
    scopeLabel: isAr ? "نطاق البحث" : "Search in",
    none: isAr ? "لا نتائج مطابقة. جرّب صياغة أخرى." : "No matching results.",
    advisorView: isAr ? "رأي المستشار القانوني" : "Legal consultant view",
    goArticle: (n) => isAr ? `اذهب للمادة ${n} داخل القانون` : `Go to Art. ${n}`,
    read: isAr ? "قراءة" : "Read",
    copy: isAr ? "نسخ المادة" : "Copy article",
    copied: isAr ? "تم النسخ" : "Copied",
    askSami: isAr ? "اسأل سامي" : "Ask Sami",
    reason: isAr ? "لماذا يناسب:" : "Why it fits:",
    resultsFor: isAr ? "النتائج" : "Results",
  }), [isAr]);

  const laws = useMemo(() => lawHits.filter((h) => !isReg(h)), [lawHits]);
  const regs = useMemo(() => lawHits.filter((h) => isReg(h)), [lawHits]);

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const query = q.trim();
    if (query.length < 2) return;
    const needLaws = scope !== "principle";
    const needPrinc = scope === "all" || scope === "principle";
    setLoading(true);
    setDone(false);
    setSp({ q: query }, { replace: true });
    try {
      const [lawRes, pRes] = await Promise.allSettled([
        needLaws
          ? api.get("/library/consult", { params: { q: query, limit: 12 } })
          : Promise.resolve({ data: { hits: [], answer: "" } }),
        needPrinc
          ? api.get("/judgments/ai-search", { params: { q: query, limit: 8 } })
          : Promise.resolve({ data: { results: [] } }),
      ]);
      if (needLaws && lawRes.status === "fulfilled") {
        setLawHits((lawRes.value.data?.hits || []).filter((h) => h.kind === "law"));
        setAnswer(lawRes.value.data?.answer || "");
      } else {
        setLawHits([]); setAnswer("");
      }
      setPrinciples(needPrinc && pRes.status === "fulfilled" ? (pRes.value.data?.results || []) : []);
      setTab(scope);
    } finally {
      setLoading(false);
      setDone(true);
    }
  };

  const copyArticle = (key, text) => {
    const fin = () => {
      setCopiedId(key);
      toast.success(t.copied);
      window.setTimeout(() => setCopiedId((c) => (c === key ? null : c)), 1500);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text || "").then(fin).catch(fin);
    else fin();
  };

  const askSamiLaw = (h) => {
    const base = isAr ? `بخصوص «${h.title}»` : `Regarding "${h.title}"`;
    const art = h.article_number > 0 ? (isAr ? ` المادة ${h.article_number}.` : ` Art. ${h.article_number}.`) : ".";
    const tail = isAr ? " اشرح لي الحكم وما ينطبق منه." : " Explain it.";
    navigate(`/consultant?q=${encodeURIComponent(base + art + tail)}&ask=1`);
  };

  const askSamiPrinciple = (r) => {
    const query = isAr
      ? `بخصوص مبدأ محكمة التمييز: «${r.case_number || ""}». اشرح لي هذا المبدأ وما ينطبق منه مع ذكر المواد والقوانين ذات الصلة.`
      : `Regarding cassation principle: "${r.case_number || ""}". Explain it.`;
    navigate(`/consultant?q=${encodeURIComponent(query)}&ask=1`);
  };

  const LawCard = ({ h }) => (
    <div data-testid={`search-law-${h.id}`} className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="flex items-start gap-3" dir="rtl">
        <ScrollText className="w-4 h-4 mt-1 shrink-0 text-slate-400" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/laws/${h.id}`} className="font-display text-base lg:text-lg hover:text-[var(--gold-deep)] transition-colors">
              {h.title}
            </Link>
            {isReg(h) && (
              <span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ background: "var(--navy)" }}>
                {isAr ? "لائحة/قرار" : "Regulation"}
              </span>
            )}
            {h.article_number > 0 && (
              <span className="text-[11px] font-mono text-slate-500">{isAr ? `مادة ${h.article_number}` : `Art. ${h.article_number}`}</span>
            )}
          </div>
          <div className="mt-3 text-[15px] text-slate-800 leading-[2] whitespace-pre-wrap rounded-sm border border-slate-200 bg-slate-50/60 p-4 max-h-72 overflow-y-auto">
            {h.text || h.snippet}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Link
              to={h.article_number > 0 ? `/laws/${h.id}#article-${h.article_number}` : `/laws/${h.id}`}
              data-testid={`search-law-open-${h.id}`}
              className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] rounded-sm text-white transition-colors"
              style={{ background: "var(--navy)" }}
            >
              <BookOpen className="w-3.5 h-3.5" /> {h.article_number > 0 ? t.goArticle(h.article_number) : t.read}
            </Link>
            <button
              type="button"
              onClick={() => copyArticle(`${h.id}-${h.article_number}`, h.text || h.snippet)}
              data-testid={`search-law-copy-${h.id}`}
              className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] border transition-colors hover:bg-[var(--beige)]"
              style={{ borderColor: "var(--gold-deep)", color: copiedId === `${h.id}-${h.article_number}` ? "#059669" : "var(--gold-deep)" }}
            >
              {copiedId === `${h.id}-${h.article_number}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedId === `${h.id}-${h.article_number}` ? t.copied : t.copy}
            </button>
            <button
              type="button"
              onClick={() => askSamiLaw(h)}
              data-testid={`search-law-asksami-${h.id}`}
              className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] rounded-sm text-white transition-colors"
              style={{ background: "var(--gold-deep)" }}
            >
              <Sparkles className="w-3.5 h-3.5" /> {t.askSami}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const PrincipleCard = ({ r }) => (
    <div data-testid={`search-principle-${r.id}`} className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="flex items-start gap-3" dir="rtl">
        <Gavel className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--gold-deep)" }} />
        <div className="min-w-0 flex-1">
          <Link to={`/judgments/${r.id}${r.matched?.length ? `?q=${encodeURIComponent(r.matched[0])}` : ""}`} className="font-display text-base lg:text-lg hover:text-[var(--gold-deep)] transition-colors block">
            {r.case_number}
          </Link>
          {r.court && <p className="text-xs text-slate-500 mt-0.5">{r.court}</p>}
          {r.reason && (
            <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--gold-deep)" }}>
              <span className="text-slate-400">{t.reason} </span>{r.reason}
            </p>
          )}
          {r.excerpt && (
            <div className="mt-2 text-[15px] text-slate-800 leading-[2] whitespace-pre-wrap rounded-sm border border-slate-200 bg-slate-50/60 p-4 max-h-60 overflow-y-auto">
              {r.excerpt}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Link
              to={`/judgments/${r.id}${r.matched?.length ? `?q=${encodeURIComponent(r.matched[0])}` : ""}`}
              data-testid={`search-principle-open-${r.id}`}
              className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] rounded-sm text-white transition-colors"
              style={{ background: "var(--navy)" }}
            >
              <BookOpen className="w-3.5 h-3.5" /> {t.read}
            </Link>
            <button
              type="button"
              onClick={() => askSamiPrinciple(r)}
              data-testid={`search-principle-asksami-${r.id}`}
              className="inline-flex items-center gap-1.5 px-3 h-9 text-[11px] uppercase tracking-[0.14em] rounded-sm text-white transition-colors"
              style={{ background: "var(--gold-deep)" }}
            >
              <Sparkles className="w-3.5 h-3.5" /> {t.askSami}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const showLaws = tab === "all" || tab === "law";
  const showRegs = tab === "all" || tab === "regulation";
  const showPrinc = tab === "all" || tab === "principle";
  const total = lawHits.length + principles.length;

  return (
    <div data-testid="unified-search-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Search className="w-6 h-6" style={{ color: "var(--gold-deep)" }} />
          <h1 className="font-display text-3xl lg:text-4xl">{t.title}</h1>
        </div>
        <p className="text-slate-600 leading-relaxed max-w-2xl">{t.lead}</p>
      </div>

      <form onSubmit={runSearch} className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            dir="auto"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.placeholder}
            data-testid="unified-search-input"
            className="rounded-sm h-12 ps-9 text-base"
          />
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger
            data-testid="unified-search-scope"
            className="h-12 rounded-sm sm:w-56 text-sm"
            aria-label={t.scopeLabel}
          >
            <SelectValue placeholder={t.scopeLabel} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="scope-all">{t.all}</SelectItem>
            <SelectItem value="law" data-testid="scope-law">{t.laws}</SelectItem>
            <SelectItem value="regulation" data-testid="scope-regulation">{t.regs}</SelectItem>
            <SelectItem value="principle" data-testid="scope-principle">{t.princ}</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="submit"
          disabled={loading || q.trim().length < 2}
          data-testid="unified-search-submit"
          className="inline-flex items-center justify-center gap-2 h-12 px-7 text-sm rounded-sm transition-colors disabled:opacity-60"
          style={{ background: "var(--navy)", color: "#fff" }}
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.thinking}</> : <><Sparkles className="w-4 h-4" /> {t.btn}</>}
        </button>
      </form>

      {answer && (
        <div data-testid="unified-search-answer" className="mb-8 rounded-sm p-6" style={{ background: "var(--navy)" }} dir="rtl">
          <p className="eyebrow text-white/50 mb-2">{t.advisorView}</p>
          <p className="text-white/90 leading-[2] whitespace-pre-wrap text-[15px]">{answer}</p>
        </div>
      )}

      {done && (
        <>
          <div className="mb-6 inline-flex flex-wrap border border-slate-200 bg-white rounded-sm overflow-hidden" data-testid="unified-search-tabs">
            {[
              ["all", t.all, total],
              ["law", t.laws, laws.length],
              ["regulation", t.regs, regs.length],
              ["principle", t.princ, principles.length],
            ].map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                data-testid={`unified-tab-${key}`}
                onClick={() => setTab(key)}
                className={`px-4 h-10 text-xs uppercase tracking-[0.14em] transition-colors border-s border-slate-200 first:border-s-0 ${tab === key ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
                style={tab === key ? { background: "var(--navy)" } : {}}
              >
                {label} <span className="opacity-60">({count})</span>
              </button>
            ))}
          </div>

          {total === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500">{t.none}</div>
          ) : (
            <div className="space-y-4">
              {showLaws && laws.map((h) => <LawCard key={`l-${h.id}-${h.article_number}`} h={h} />)}
              {showRegs && regs.map((h) => <LawCard key={`r-${h.id}-${h.article_number}`} h={h} />)}
              {showPrinc && principles.map((r) => <PrincipleCard key={`p-${r.id}`} r={r} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
